import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCompass } from 'react-icons/fa';

const Banner = styled(motion.div)`
  width: 100%;
  margin-bottom: 1.5rem;
  padding: 1.5rem 1.35rem;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.04) 0%,
    rgba(18, 18, 28, 0.6) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.22);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--type-accent, var(--primary-color));
  }

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 1.75rem 2rem;
  }
`;

const Copy = styled.div`
  flex: 1;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--type-accent, var(--primary-color));
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 0.45rem;
`;

const Title = styled.h2`
  font-family: var(--font-display);
  font-size: 1.45rem;
  color: var(--text-primary);
  margin-bottom: 0.4rem;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.02em;
`;

const Description = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0;
  max-width: 520px;
  font-size: 0.98rem;
`;

const FeaturePills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 0.85rem;
`;

const Pill = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  padding: 0.3rem 0.65rem;
`;

const ExploreButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: var(--type-accent, var(--primary-color));
  color: #0C0F14;
  font-weight: 700;
  font-size: 0.95rem;
  padding: 0.85rem 1.35rem;
  border-radius: 12px;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  flex-shrink: 0;
`;

const PremiumWelcomeBanner = ({ primaryTypeTitle, onExplore }) => (
  <Banner
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45 }}
    role="region"
    aria-label="Your founder playbook"
  >
    <Copy>
      <Eyebrow>
        <FaCompass aria-hidden="true" />
        Your playbook
      </Eyebrow>
      <Title>
        {primaryTypeTitle
            ? `${primaryTypeTitle}, built for this week`
          : 'Your personalized founder playbook'}
      </Title>
      <Description>
        Stress patterns, a 3-step plan, and meeting scripts tailored to how you lead under pressure. Pick one behavior to install this week.
      </Description>
      <FeaturePills>
        <Pill>Week plan</Pill>
        <Pill>Stress mode</Pill>
        <Pill>Scripts</Pill>
        <Pill>Check-in</Pill>
      </FeaturePills>
    </Copy>
    {onExplore && (
      <ExploreButton type="button" onClick={onExplore} whileTap={{ scale: 0.97 }}>
        Open playbook
      </ExploreButton>
    )}
  </Banner>
);

export default PremiumWelcomeBanner;
